import {entries, groupBy, mapObject} from '@benchling/js-utils';
import {BaseUnitType, Term, unitFromTerms} from '@benchling/units';
import {css, StyleSheet} from 'aphrodite';
import cn from 'classnames';
import React, {useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BLUE_POWDER} from 'ui-library/Colors';
import TextField from 'ui-library/inputs/TextField';

import FormulaCanvas from './FormulaCanvas';
import {CreateUnitTypeParams} from './UnitDictionaryDAO';
import UnitTypeTermsDisplay from './UnitTypeTermsDisplay';
import {HydratedUnit} from './util';

interface UnitTypeFormProps {
  name: string;
  onNameChange: (name: string) => void;
  simplifiedTerms: Array<Term<BaseUnitType>>;
  numeratorUnits: Array<HydratedUnit>;
  onSetNumeratorUnits: React.Dispatch<React.SetStateAction<Array<HydratedUnit>>>;
  denominatorUnits: Array<HydratedUnit>;
  onSetDenominatorUnits: React.Dispatch<React.SetStateAction<Array<HydratedUnit>>>;
  baseUnitName?: string;
  onSetBaseUnitName?: (name: string) => void;
  baseUnitSymbol?: string;
  onSetBaseUnitSymbol?: (symbol: string) => void;
  errorMessage: string;
  warningMessages?: Array<string>;
}

export function formatOriginalFormula(
  unitTerms: Array<{exponent: number; value: HydratedUnit}>
): CreateUnitTypeParams['originalFormula'] {
  const formulaTerms = unitTerms.map((term) => ({exponent: term.exponent, value: term.value.id}));

  const termsByValue = groupBy(formulaTerms, (term) => term.value);
  const netExponents = mapObject(termsByValue, (terms) =>
    terms.map((t) => t.exponent).reduce((a, b) => a + b)
  );

  return entries(netExponents)
    .filter(([, exp]) => exp !== 0)
    .map(([value, exponent]) => ({value, exponent}));
}

export function useFormulaState(
  initialNumeratorUnits: Array<HydratedUnit>,
  initialDenominatorUnits: Array<HydratedUnit>
): {
  numeratorUnits: Array<HydratedUnit>;
  setNumeratorUnits: React.Dispatch<React.SetStateAction<Array<HydratedUnit>>>;
  denominatorUnits: Array<HydratedUnit>;
  setDenominatorUnits: React.Dispatch<React.SetStateAction<Array<HydratedUnit>>>;
  combinedTerms: Array<{value: HydratedUnit; exponent: number}>;
  simplifiedTerms: Array<Term<BaseUnitType>>;
} {
  const [numeratorUnits, setNumeratorUnits] = useState<Array<HydratedUnit>>(initialNumeratorUnits);
  const [denominatorUnits, setDenominatorUnits] = useState<Array<HydratedUnit>>(initialDenominatorUnits);

  const combinedTerms = [
    ...numeratorUnits.map((unit) => ({value: unit, exponent: 1})),
    ...denominatorUnits.map((unit) => ({value: unit, exponent: -1})),
  ];
  const simplifiedTerms = unitFromTerms('', '', combinedTerms).unitType.terms;

  return {
    numeratorUnits,
    setNumeratorUnits,
    denominatorUnits,
    setDenominatorUnits,
    combinedTerms,
    simplifiedTerms,
  };
}

export default function UnitTypeForm({
  name,
  onNameChange,
  simplifiedTerms,
  numeratorUnits,
  onSetNumeratorUnits,
  denominatorUnits,
  onSetDenominatorUnits,
  baseUnitName,
  onSetBaseUnitName,
  baseUnitSymbol,
  onSetBaseUnitSymbol,
  errorMessage,
  warningMessages,
}: UnitTypeFormProps): JSX.Element {
  return (
    <div {...getTestProps({component: 'UnitTypeForm', element: 'root'})}>
      <div className={css(styles.topRow)}>
        <TextField
          label="Unit type name"
          isRequired={true}
          className={cn('u-spacing-bottom', css(styles.nameInput))}
          value={name}
          onChange={(e) => {
            onNameChange(e.target.value);
          }}
          dataTestAttrs={{component: 'UnitTypeForm', element: 'unitTypeName'}}
          wrapperProps={{
            ...getTestProps({component: 'UnitTypeForm', element: 'unitTypeNameWrapper'}),
          }}
        />
        <div className={css(styles.dimensionsalFormulaSection)}>
          <b>Dimensional Formula</b>
          {simplifiedTerms.length > 0 && <UnitTypeTermsDisplay terms={simplifiedTerms} />}
        </div>
      </div>
      <label className="fieldLabel--label">Formula</label>
      <FormulaCanvas
        numeratorUnits={numeratorUnits}
        denominatorUnits={denominatorUnits}
        setNumeratorUnits={onSetNumeratorUnits}
        setDenominatorUnits={onSetDenominatorUnits}
      />
      {onSetBaseUnitName && onSetBaseUnitSymbol && (
        <div className="u-flex-display">
          <TextField
            label="Base unit name"
            isRequired={true}
            className={cn('u-spacing-top', 'u-spacing-right', css(styles.nameInput))}
            value={baseUnitName}
            onChange={(e) => {
              onSetBaseUnitName(e.target.value);
            }}
            dataTestAttrs={{component: 'UnitTypeForm', element: 'baseUnitName'}}
          />
          <TextField
            label="Base unit symbol"
            isRequired={true}
            className={cn('u-spacing-top', css(styles.symbolInput))}
            value={baseUnitSymbol}
            onChange={(e) => {
              onSetBaseUnitSymbol(e.target.value);
            }}
            dataTestAttrs={{component: 'UnitTypeForm', element: 'baseUnitSymbol'}}
          />
        </div>
      )}
      {errorMessage && errorMessage.length > 0 && (
        <div className="u-spacing-top">
          <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
        </div>
      )}
      {warningMessages &&
        warningMessages.map((msg: string, index: number) => (
          <div className="u-spacing-top" key={index}>
            <InlineBanner bannerType={InlineBannerType.WARNING} text={msg} />
          </div>
        ))}
    </div>
  );
}

const styles = StyleSheet.create({
  nameInput: {
    width: '200px',
  },
  symbolInput: {
    width: '120px',
  },
  dimensionsalFormulaSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '300px',
    height: '90px',
    backgroundColor: BLUE_POWDER,
    margin: '10px',
    padding: '10px',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});
